import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/**
 * The YouTube URL allowlist, and the app's only sanctioned caller of `bypassSecurityTrust*`.
 *
 * Training URLs are CRM-authored and the `training-detail` route param is attacker-supplied,
 * so a `javascript:` URI bound to an `<iframe [src]>` would run in the app origin. Validation
 * and trust live in one file so the second cannot be reached without the first;
 * `scripts/semgrep-rules/path-mobile-app.yml` fails the build on a bypass call anywhere else.
 */

/** Exact match — a suffix match would accept `youtube.com.evil.tld`. */
const ALLOWED_HOSTS = [
    'youtube.com',
    'www.youtube.com',
    'm.youtube.com',
    'youtube-nocookie.com',
    'www.youtube-nocookie.com',
    'youtu.be',
];

/** Video ids are exactly 11 characters of URL-safe base64. */
const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;

const MAX_URL_LENGTH = 2048;

/** Player options this app may put on an embed URL. */
export interface YoutubeEmbedOptions {
    autoplay?: boolean;
    showinfo?: boolean;
    controls?: boolean;
}

/** Rendered in this order so the URL is stable. */
const OPTION_KEYS: Array<keyof YoutubeEmbedOptions> = ['autoplay', 'showinfo', 'controls'];

/** A validated embed URL, in both forms a template needs. */
export interface SafeYoutubeEmbed {
    url: string;
    sanitizedUrl: SafeResourceUrl;
}

/**
 * Normalise `raw` to a canonical embed URL, or null if it is not an allowlisted YouTube video.
 *
 * Recognises `youtu.be/{id}`, `/embed/{id}` and `/watch?v={id}` on an allowlisted host, and
 * rejects everything else. The result is rebuilt from the validated id, so no part of `raw`
 * survives into it. Exported for direct testing; callers use `YoutubeEmbedService`.
 */
export function toYoutubeEmbedUrl(raw: unknown, options: YoutubeEmbedOptions = {}): string | null {
    const videoId = extractVideoId(raw);

    return videoId === null ? null : `https://www.youtube.com/embed/${videoId}${buildQuery(options)}`;
}

function extractVideoId(raw: unknown): string | null {
    // A type check, not a coercion: an object with a URL-producing toString() is still untrusted.
    if (typeof raw !== 'string') {
        return null;
    }

    const trimmed = raw.trim();

    if (trimmed === '' || trimmed.length > MAX_URL_LENGTH) {
        return null;
    }

    let url: URL;

    try {
        url = new URL(trimmed);
    } catch {
        // Relative and protocol-relative inputs are rejected, not resolved against the app
        // origin — resolving them would make them same-origin.
        return null;
    }

    // Parsing is not the check: `javascript:alert(1)` parses fine. The scheme is.
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        return null;
    }

    if (!ALLOWED_HOSTS.includes(url.hostname)) {
        return null;
    }

    // Empty segments dropped, so a trailing slash is tolerated but extra segments are not.
    const segments = url.pathname.split('/').filter((segment) => segment !== '');
    const candidate = candidateIdFrom(url, segments);

    return candidate !== null && VIDEO_ID.test(candidate) ? candidate : null;
}

function candidateIdFrom(url: URL, segments: string[]): string | null {
    if (url.hostname === 'youtu.be') {
        return segments.length === 1 ? segments[0] : null;
    }

    if (segments.length === 2 && segments[0] === 'embed') {
        return segments[1];
    }

    if (segments.length === 1 && segments[0] === 'watch') {
        return url.searchParams.get('v');
    }

    return null;
}

function buildQuery(options: YoutubeEmbedOptions): string {
    const params = new URLSearchParams();

    OPTION_KEYS.forEach((key) => {
        const value = options[key];

        if (value !== undefined) {
            params.set(key, value ? '1' : '0');
        }
    });

    const query = params.toString();

    return query === '' ? '' : `?${query}`;
}

@Injectable({ providedIn: 'root' })
export class YoutubeEmbedService {

    constructor(private domSanitizer: DomSanitizer) {}

    /** Validate `raw` against the allowlist, or return null if it is not a YouTube video URL. */
    toSafeEmbed(raw: unknown, options: YoutubeEmbedOptions = {}): SafeYoutubeEmbed | null {
        const url = toYoutubeEmbedUrl(raw, options);

        if (url === null) {
            return null;
        }

        // Safe by construction: `url` was rebuilt from a validated id, so none of the
        // caller's bytes are in it.
        return { url, sanitizedUrl: this.domSanitizer.bypassSecurityTrustResourceUrl(url) };
    }
}
